import type {
  SocialBestTimeSlot,
  SocialContent,
  SocialContentPost,
  SocialDateRange,
  SocialPlatform,
  SocialPublishedPost,
  SocialStory,
  StorySource,
} from '../../types/socialReporting.js';
import { isMeasurablePlatform } from './socialOverview.js';

// ---------------------------------------------------------------------------
// Instagram Stories — the source-state gate (mutation-tested)
// ---------------------------------------------------------------------------

// ⚠️ THE SOURCE-STATE GATE. A story's insights are shown ONLY when its source is
// 'live' or 'cached'; 'unavailable' means the account was connected after the 24h
// expiry, so insights were never captured — rendered as "not captured", NEVER 0
// (a measured story CAN legitimately read 0 when a count floors below Meta's <5
// privacy threshold, which is distinct from "unavailable"). Flipping this gate to
// treat 'unavailable' as measured must fail a test.
export function isStoryMeasured(source: StorySource): boolean {
  return source !== 'unavailable';
}

// Counts under 5 may floor to 0 on measured stories (Meta privacy) — stated in the UI.
export const STORY_PRIVACY_NOTE =
  'Instagram hides very small counts, so figures under 5 may show as 0.';

export interface StoriesView {
  measured: SocialStory[];
  unavailable: SocialStory[];
  hasAny: boolean;
}

/** Split stories into measured (live/cached) vs unavailable, newest first. */
export function buildStoriesView(stories: SocialStory[]): StoriesView {
  const byNewest = (a: SocialStory, b: SocialStory) =>
    (b.postedAt ?? '').localeCompare(a.postedAt ?? '');
  const measured = stories.filter((s) => isStoryMeasured(s.source)).sort(byNewest);
  const unavailable = stories.filter((s) => !isStoryMeasured(s.source)).sort(byNewest);
  return { measured, unavailable, hasAny: stories.length > 0 };
}

// Organic Social Reporting — Content tab aggregation (pure).
//
// Ranking (top posts), format bucketing, posting-cadence assembly and
// best-time-to-post heatmap shaping — all pure so they're unit-testable off the
// observed fixtures (observed-2026-07-22). Engagement is the shared definition
// (postEngagement, already applied server-side into SocialContentPost.engagement),
// so the displayed figure and the ordering never disagree.
//
// ═══ THE GBP ENGAGEMENT GATE (mutation-tested) ═══
// Google Business has NO post analytics (probe B2). It appears in posting CADENCE
// (volume) only — never in the engagement-ranked top posts / by-format-engagement
// sections. The server only ever sends FB/IG posts, and
// contentPostsForEngagement re-applies the gate here so a stray GBP post can never
// be ranked by an engagement figure we cannot measure.

// ---------------------------------------------------------------------------
// the engagement gate
// ---------------------------------------------------------------------------

/** Posts that can be ranked by engagement — FB/IG only (never GBP). */
export function contentPostsForEngagement(posts: SocialContentPost[]): SocialContentPost[] {
  return posts.filter((p) => isMeasurablePlatform(p.platform));
}

// ---------------------------------------------------------------------------
// ranking
// ---------------------------------------------------------------------------

/** Posts ordered by engagement, descending. Stable within equal engagement. */
export function rankByEngagement(posts: SocialContentPost[]): SocialContentPost[] {
  return [...posts]
    .map((p, i) => ({ p, i }))
    .sort((a, b) => b.p.engagement - a.p.engagement || a.i - b.i)
    .map((x) => x.p);
}

export interface RankedContent {
  /** Top N by engagement. */
  top: SocialContentPost[];
}

// Top posts only — the Underperformers section was retired in the Content redesign
// (2026-07-23); we no longer surface a "what didn't land" list.
export function rankContent(posts: SocialContentPost[], topN: number): RankedContent {
  const ranked = rankByEngagement(contentPostsForEngagement(posts));
  return { top: ranked.slice(0, topN) };
}

// ---------------------------------------------------------------------------
// format bucketing (feed / reel by platform)
// ---------------------------------------------------------------------------

export type PostFormat = 'feed' | 'reel';

/**
 * Feed vs Reel from the media type. IG `contentType` is only ever 'story' (feed
 * vs reel is inferred from media — a video is a Reel), and FB reels are videos
 * too, so a video post is a Reel and everything else is Feed. Stories are
 * excluded upstream (not in this data yet).
 */
export function postFormat(post: SocialContentPost): PostFormat {
  return (post.mediaType ?? '').toLowerCase() === 'video' ? 'reel' : 'feed';
}

export interface FormatBucket {
  platform: SocialPlatform;
  format: PostFormat;
  count: number;
  totalEngagement: number;
  avgEngagement: number;
  totalViews: number;
  /** Avg over posts that reported a view count (nulls excluded), null if none did. */
  avgViews: number | null;
}

export function bucketByFormat(posts: SocialContentPost[]): FormatBucket[] {
  const map = new Map<string, { platform: SocialPlatform; format: PostFormat; posts: SocialContentPost[] }>();
  for (const post of contentPostsForEngagement(posts)) {
    const format = postFormat(post);
    const key = `${post.platform}_${format}`;
    const bucket = map.get(key) ?? { platform: post.platform, format, posts: [] };
    bucket.posts.push(post);
    map.set(key, bucket);
  }
  const order: SocialPlatform[] = ['facebook', 'instagram'];
  const formatOrder: PostFormat[] = ['feed', 'reel'];
  return [...map.values()]
    .map(({ platform, format, posts: ps }) => {
      const totalEngagement = ps.reduce((s, p) => s + p.engagement, 0);
      const withViews = ps.filter((p) => p.views !== null);
      const totalViews = ps.reduce((s, p) => s + (p.views ?? 0), 0);
      return {
        platform,
        format,
        count: ps.length,
        totalEngagement,
        avgEngagement: totalEngagement / ps.length,
        totalViews,
        avgViews: withViews.length > 0 ? totalViews / withViews.length : null,
      };
    })
    .sort(
      (a, b) => order.indexOf(a.platform) - order.indexOf(b.platform) || formatOrder.indexOf(a.format) - formatOrder.indexOf(b.format),
    );
}

// ---------------------------------------------------------------------------
// posting cadence (posts per week by platform + engagement overlay)
// ---------------------------------------------------------------------------

// Monday-start ISO week key for a YYYY-MM-DD day.
function weekStartOf(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  const dow = (d.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

function enumerateWeeks(range: SocialDateRange): string[] {
  const out: string[] = [];
  let t = Date.parse(`${weekStartOf(range.from)}T00:00:00Z`);
  const end = Date.parse(`${range.to}T00:00:00Z`);
  if (Number.isNaN(t) || Number.isNaN(end)) return out;
  while (t <= end) {
    out.push(new Date(t).toISOString().slice(0, 10));
    t += 7 * 86_400_000;
  }
  return out;
}

export interface CadenceWeek {
  weekStart: string;
  facebook: number;
  instagram: number;
  googlebusiness: number;
  /** Total volume across platforms (all posts published that week). */
  volume: number;
  /** Engagement of the FB/IG analytics posts published that week (GBP contributes none). */
  engagement: number;
}

function inRange(day: string, range: SocialDateRange): boolean {
  return day >= range.from && day <= range.to;
}

/**
 * Posts-per-week volume (by platform, from the mirror ∪ external union) with the
 * FB/IG engagement overlay — so "did volume match results?" reads at a glance.
 * Every week in the range appears (empty weeks are 0, an honest gap).
 */
export function weeklyCadence(
  publishedPosts: SocialPublishedPost[],
  analyticsPosts: SocialContentPost[],
  range: SocialDateRange,
): CadenceWeek[] {
  const weeks = new Map<string, CadenceWeek>();
  for (const w of enumerateWeeks(range)) {
    weeks.set(w, { weekStart: w, facebook: 0, instagram: 0, googlebusiness: 0, volume: 0, engagement: 0 });
  }
  for (const post of publishedPosts) {
    const day = post.publishedAt.slice(0, 10);
    if (!inRange(day, range)) continue;
    const week = weeks.get(weekStartOf(day));
    if (!week) continue;
    week[post.platform] += 1;
    week.volume += 1;
  }
  for (const post of contentPostsForEngagement(analyticsPosts)) {
    if (!post.publishedAt) continue;
    const day = post.publishedAt.slice(0, 10);
    if (!inRange(day, range)) continue;
    const week = weeks.get(weekStartOf(day));
    if (week) week.engagement += post.engagement;
  }
  return [...weeks.values()].sort((a, b) => a.weekStart.localeCompare(b.weekStart));
}

// ---------------------------------------------------------------------------
// best-time-to-post heatmap
// ---------------------------------------------------------------------------

export const HEATMAP_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export interface HeatmapCell {
  avgEngagement: number;
  postCount: number;
}

export interface Heatmap {
  /** [day 0..6 (Mon..Sun)][hour 0..23] → cell or null (no data). */
  grid: Array<Array<HeatmapCell | null>>;
  /** Max avgEngagement across cells (for colour scaling); 0 when empty. */
  maxAvg: number;
  /** True when no slot had any data. */
  isEmpty: boolean;
}

/**
 * Shape slots into a 7×24 grid, shifting each slot from Zernio's UTC hour to the
 * viewer's local time (offset injected, so it's testable and DST-agnostic here).
 * The offset is rounded to whole hours — a heatmap cell is hour-granular, and the
 * handful of half-hour zones don't warrant sub-cell interpolation. Slots that
 * collide on a cell after the shift are merged (post-count-weighted average).
 */
export function buildHeatmap(slots: SocialBestTimeSlot[], tzOffsetMinutes: number): Heatmap {
  const offsetHours = Math.round(tzOffsetMinutes / 60);
  // Accumulate weighted sums per cell so colliding slots merge honestly.
  const sumEng: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0));
  const sumCount: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0));
  let any = false;
  for (const slot of slots) {
    if (slot.dayOfWeek < 0 || slot.dayOfWeek > 6 || slot.hour < 0 || slot.hour > 23) continue;
    const total = slot.hour + offsetHours;
    const localHour = ((total % 24) + 24) % 24;
    const dayDelta = Math.floor(total / 24);
    const localDay = (((slot.dayOfWeek + dayDelta) % 7) + 7) % 7;
    const weight = slot.postCount > 0 ? slot.postCount : 1;
    sumEng[localDay]![localHour]! += slot.avgEngagement * weight;
    sumCount[localDay]![localHour]! += weight;
    any = true;
  }
  let maxAvg = 0;
  const grid = sumCount.map((row, d) =>
    row.map((count, h) => {
      if (count === 0) return null;
      const avg = sumEng[d]![h]! / count;
      if (avg > maxAvg) maxAvg = avg;
      return { avgEngagement: avg, postCount: count };
    }),
  );
  return { grid, maxAvg, isEmpty: !any };
}

// ---------------------------------------------------------------------------
// top-level view (excluding the heatmap, which needs the browser tz offset)
// ---------------------------------------------------------------------------

export interface ContentView {
  top: SocialContentPost[];
  formatBuckets: FormatBucket[];
  cadence: CadenceWeek[];
  /** Platforms that actually have cadence volume (for the stacked-bar series). */
  cadencePlatforms: SocialPlatform[];
  /** Any FB/IG analytics post at all — else the engagement sections are empty. */
  hasEngagementData: boolean;
  /** GBP connected → show the "cadence only, no post analytics" note. */
  gbpConnected: boolean;
  asOf: string | null;
  failures: SocialContent['failures'];
}

const ALL_PLATFORMS: SocialPlatform[] = ['facebook', 'instagram', 'googlebusiness'];

export function buildContentView(data: SocialContent, opts: { topN?: number } = {}): ContentView {
  const topN = opts.topN ?? 6;
  const ranked = rankContent(data.posts, topN);
  const cadence = weeklyCadence(data.publishedPosts, data.posts, data.range);
  const cadencePlatforms = ALL_PLATFORMS.filter((p) => cadence.some((w) => w[p] > 0));
  const engagementPosts = contentPostsForEngagement(data.posts);
  return {
    top: ranked.top,
    formatBuckets: bucketByFormat(data.posts),
    cadence,
    cadencePlatforms,
    hasEngagementData: engagementPosts.length > 0,
    gbpConnected: data.connected.googlebusiness,
    asOf: data.lastSync,
    failures: data.failures,
  };
}

// ---------------------------------------------------------------------------
// display helpers
// ---------------------------------------------------------------------------

/** "1m 05s" / "12s" from seconds (IG Reel watch times). */
export function formatWatchSeconds(sec: number): string {
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

/** "9 AM" style label for a 0-23 hour. */
export function formatHour(hour: number): string {
  const h = ((hour % 24) + 24) % 24;
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}
